import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useCurrentUser } from '@/api/hooks'

// Phone alerts for store chats (USA-1483): the service worker, what this browser supports, and
// the app icon number. The on/off control is in phone-alerts.tsx.

const WORKER_URL = '/sw.js'

/** Register the alerts service worker once the dashboard loads; it only shows alerts. */
export function registerAlertsWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.register(WORKER_URL).catch(() => undefined)
}

export type Support = 'supported' | 'install' | 'unsupported'

function isAppleMobile() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function alertSupport(): Support {
  if (typeof window === 'undefined') return 'unsupported'
  const push = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (push) return 'supported'
  // Safari on an iPhone gets push only once the dashboard is on the Home Screen.
  return isAppleMobile() && !isStandalone() ? 'install' : 'unsupported'
}

/** Whether the signed-in staff member answers store chats. */
export function useIsChatResponder() {
  const { data: user } = useCurrentUser()
  return !!user?.store_chat_responder
}

/** Tapping an alert while the dashboard is open moves it to that chat (the service worker asks). */
export function useAlertNavigation() {
  const navigate = useNavigate()
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null
      if (data?.type === 'open-chat' && data.url?.startsWith('/')) void navigate({ href: data.url })
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])
}

/** The number on the app icon: chats waiting on a person, for responders. */
export function setAppBadge(count: number) {
  const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined)
  else void nav.clearAppBadge?.().catch(() => undefined)
}
